import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  rewardLeaderboardTransaction,
  updateCoinBalance,
  walletBalanceList,
} from "store/actions";
import Table from "components/Table";
import Columns from "./Columns";
import FormikForm from "components/shared/FormikForm";
import WalletBalancePop from "components/Modals/WalletBalancePop";
import Validation from "./Validation";
import { checkPermissions } from "helpers/common";
import { toast } from "react-toastify";

const InputFieldsForSearch = [
  {
    name: "search",
    type: "text",
    label: "",
    placeholder: "Search...",
  },
];

const initialValuesForFilter = {
  search: "",
};

const List = ({ userDetails }) => {
  const dispatch = useDispatch();
  const { coinsList } = useSelector(({ Game }) => Game);

  const { walletBalanceListData, loading, totalWalletBalanceListData } =
    useSelector(({ Users }) => Users);
  const [walletBalance, setWalletBalance] = useState(false);
  const [loadingWallet, setLoadingWallet] = useState(false);

  const [columns] = useState(Columns({})); // set once
  const [filter, setFilter] = useState({
    page: 1,
    limit: 10,
    order: -1,
    orderBy: "date_created_utc",
    search: "",
    userId: "",
  });

  const { user } = useSelector((state) => state.Auth);
  const [permissions, setPermissions] = useState({
    update: false,
  });

  useEffect(() => {
    if (user?.permissions) {
      setPermissions({
        update: checkPermissions(["update_user_balance"], user?.permissions),
      });
    }
  }, [user]);

  // ⛔ No setColumns here → prevents infinite loop

  useEffect(() => {
    if (!userDetails?._id) return;

    // update userId first time
    setFilter((prev) => ({
      ...prev,
      userId: userDetails._id,
    }));
  }, [userDetails]);

  // 🔥 CALL API ONLY WHEN FILTER IS READY
  useEffect(() => {
    if (!filter?.userId) return;
    dispatch(walletBalanceList({ data: filter }));
  }, [filter]);

  const handleFilterForm = (info) => {
    setFilter((prev) => ({
      ...prev,
      search: info.search,
      page: 1,
    }));
  };

  const walletInitialValues = {
    coin: "",
    amount: "",
    type: "",
    transactionType: "",
    comment: "",
  };
  const coinOptions = useMemo(() => {
    if (!coinsList?.length) return [];
    return coinsList.map((coin) => ({
      label: `${coin.name} (${coin.symbol.toUpperCase()})`,
      value: JSON.stringify({
        coinName: coin.name,
        coinSymbol: coin.symbol,
        coinIcon: coin.icon,
      }),
    }));
  }, [coinsList]);

  const walletInputFields = useMemo(() => {
    return [
      {
        name: "transactionType",
        label: "Transaction Type",
        type: "select",
        placeholder: "Select Transaction Type",
        options: [
          { value: "Credit", label: "Credit" },
          { value: "Debit", label: "Debit" },
        ],
      },
      {
        name: "coin",
        label: "Coin",
        type: "select",
        placeholder: "Select Coin",
        options: coinOptions,
      },
      {
        name: "amount",
        label: "Amount",
        type: "number",
        placeholder: "Enter Amount",
        min: 0,
        step: "any",
      },
      {
        name: "type",
        label: "Type",
        type: "select",
        placeholder: "Select Type",
        options: [
          { value: "Admin Reward", label: "Admin Reward" },
          { value: "Admin Adjustment", label: "Admin Adjustment" },
        ],
      },

      {
        name: "comment",
        label: "Comment",
        type: "textarea",
        placeholder: "Enter comment...",
      },
    ];
  }, [coinOptions]);

  const handleWalletConfirm = (values, { setSubmitting, resetForm }) => {
    try {
      const selectedCoin = JSON.parse(values.coin);

      const payload = {
        userId: userDetails?._id,
        coinName: selectedCoin.coinName,
        coinSymbol: selectedCoin.coinSymbol,
        coinIcon: selectedCoin.coinIcon,
        amount: Number(values.amount),
        type: values.type,
        transactionType: values.transactionType,
        comment: values.comment,
      };

      setLoadingWallet(true);

      dispatch(
        updateCoinBalance({
          data: payload,
          callback: (err, res) => {
            setLoadingWallet(false);
            setSubmitting(false);

            if (err) {
              toast.error(err || "Something went wrong!");
              return;
            }

            toast.success(res?.message);

            dispatch(walletBalanceList({ data: filter }));
            resetForm();
            setWalletBalance(false);
          },
        }),
      );
    } catch (err) {
      console.error(err);
      setLoadingWallet(false);
      setSubmitting(false);
    }
  };

  return (
    <>
      {walletBalance && (
        <WalletBalancePop
          show={walletBalance}
          handleClose={() => setWalletBalance(false)}
          title="Wallet Balance"
          loading={loadingWallet}
          initialValues={walletInitialValues}
          inputFields={walletInputFields}
          handleConfirm={handleWalletConfirm}
          Validation={Validation}
        />
      )}
      <div className="section-body">
        <div className="card">
          <div className="row justify-content-between">
            <div className="col-12">
              <div className="card-header">
                <div className="row w-100">
                  {permissions?.update && (
                    <div className="py-2 col-12 d-flex align-items-center justify-content-end mb-3">
                      <button
                        onClick={() => setWalletBalance(true)}
                        className="btn btn-primary w-25"
                      >
                        Update Balance
                      </button>
                    </div>
                  )}
                  <FormikForm
                    initialValues={initialValuesForFilter}
                    onSubmit={handleFilterForm}
                    loading={loading}
                    inputFields={InputFieldsForSearch}
                    buttonText={"Search"}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {walletBalanceListData?.length > 0 && (
          <Table
            data={walletBalanceListData}
            columns={columns}
            setFilter={setFilter}
            total={totalWalletBalanceListData}
            filter={filter}
          />
        )}

        {!loading && walletBalanceListData?.length === 0 && (
          <p style={{ textAlign: "center" }}>No Data Found</p>
        )}
      </div>
    </>
  );
};

export default List;
